import { create } from 'zustand';
import type { Session } from '@supabase/supabase-js';
import type { Profile } from '@asi/shared';
import type { Permission } from '@asi/shared';
import { ROLE_PERMISSIONS } from '@asi/shared';
import { supabase } from '@/services/supabase';
import { checkLoginGuard, reportLoginGuard } from '@/services/login-guard';

interface AuthState {
  session: Session | null;
  profile: Profile | null;
  /** Permissions granted to the current user based on their role in the DB */
  rolePermissions: Permission[];
  loading: boolean;
  initialize: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * Updates the user's password and clears must_change_password so the forced
   * change-password gate (see App.tsx) does not reopen. Mirrors the pattern
   * already used in apps/mobile-client and apps/mobile-patrol auth-store.ts.
   */
  changePassword: (newPassword: string) => Promise<void>;
}

async function loadProfileAndPermissions(userId: string): Promise<{ profile: Profile | null; permissions: Permission[] }> {
  const { data: profile } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (!profile) return { profile: null, permissions: [] };

  const { data: role, error } = await supabase
    .from('roles')
    .select('permissions')
    .eq('name', profile.role)
    .single();

  // Fall back to the static map when the roles table doesn't exist yet
  const rolePerms: Permission[] =
    !error && role?.permissions?.length
      ? (role.permissions as Permission[])
      : (ROLE_PERMISSIONS[profile.role as keyof typeof ROLE_PERMISSIONS] ?? []);

  // Merge per-user additional permissions granted by an admin
  const additional = ((profile as unknown as { additional_permissions?: string[] }).additional_permissions ?? []) as Permission[];
  const permissions: Permission[] = additional.length
    ? [...new Set([...rolePerms, ...additional])]
    : rolePerms;

  return { profile: profile as Profile, permissions };
}

export const useAuthStore = create<AuthState>((set, get) => ({
  session: null,
  profile: null,
  rolePermissions: [],
  loading: true,

  initialize: async () => {
    // Guard: only register the auth listener once (React StrictMode double-invokes effects)
    if ((window as unknown as Record<string, unknown>).__authListenerRegistered) {
      // Still need to fetch the session so the loading flag clears
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { profile, permissions } = await loadProfileAndPermissions(session.user.id);
        set({ session, profile, rolePermissions: permissions, loading: false });
      } else {
        set({ session: null, profile: null, rolePermissions: [], loading: false });
      }
      return;
    }
    (window as unknown as Record<string, unknown>).__authListenerRegistered = true;

    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      const { profile, permissions } = await loadProfileAndPermissions(session.user.id);
      set({ session, profile, rolePermissions: permissions, loading: false });
    } else {
      set({ session: null, profile: null, rolePermissions: [], loading: false });
    }

    supabase.auth.onAuthStateChange(async (event, session) => {
      if (session) {
        // NEVER set loading: true here — it unmounts the entire app, kills all
        // Realtime subscriptions, and shows "Cargando..." on every tab switch.
        // Profile and permissions are reloaded in the background instead.
        set({ session });

        if (event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
          // Token silently refreshed or initial session restored on tab focus.
          // Session already updated above, nothing else to do.
          return;
        }

        // For SIGNED_IN / USER_UPDATED: reload profile in background (role may have changed).
        try {
          const { profile, permissions } = await loadProfileAndPermissions(session.user.id);
          set({ profile, rolePermissions: permissions });
        } catch (err) {
          console.error('[Auth] Failed to reload profile:', err);
          // Keep existing profile/permissions — app stays usable.
        }
      } else {
        set({ session: null, profile: null, rolePermissions: [], loading: false });
      }
    });
  },

  signIn: async (email, password) => {
    const guard = await checkLoginGuard(email);
    if (guard.locked) {
      const err = new Error('LOGIN_LOCKED') as Error & { code: string; retryAfterSeconds?: number };
      err.code = 'LOGIN_LOCKED';
      err.retryAfterSeconds = guard.retryAfterSeconds;
      throw err;
    }

    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      const report = await reportLoginGuard(email, false);
      if (report.locked) {
        const err = new Error('LOGIN_LOCKED') as Error & { code: string; retryAfterSeconds?: number };
        err.code = 'LOGIN_LOCKED';
        err.retryAfterSeconds = report.retryAfterSeconds;
        throw err;
      }
      throw error;
    }
    void reportLoginGuard(email, true);

    // The web dashboard is for operators only. Clients must use the mobile app.
    // Reject the session immediately if the user's profile role is 'client'.
    if (data.user) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', data.user.id)
        .single();
      if (profile?.role === 'client') {
        await supabase.auth.signOut();
        const err = new Error('CLIENT_USE_MOBILE_APP') as Error & { code: string };
        err.code = 'CLIENT_USE_MOBILE_APP';
        throw err;
      }
    }
  },

  signOut: async () => {
    // Clean up the emergency store (Realtime subscriptions + state) before
    // signing out so there are no stale WebSocket connections after logout.
    const { useEmergencyStore } = await import('./emergency-store');
    useEmergencyStore.getState().reset();
    await supabase.auth.signOut();
    set({ session: null, profile: null, rolePermissions: [] });
  },

  changePassword: async (newPassword) => {
    const userId = get().session?.user.id;
    if (!userId) throw new Error('No hay sesión activa');

    // Update the DB flag BEFORE calling updateUser (same order as mobile):
    // if onAuthStateChange fires during updateUser and reloads the profile,
    // it will already see must_change_password=false and the gate won't reopen.
    const { error: profErr } = await supabase
      .from('profiles')
      .update({ must_change_password: false })
      .eq('id', userId);
    if (profErr) throw profErr;

    const { error: authErr } = await supabase.auth.updateUser({ password: newPassword });
    if (authErr) throw authErr;

    const { profile, permissions } = await loadProfileAndPermissions(userId);
    set({ profile, rolePermissions: permissions });
  },
}));
